/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ENVIRONMENT } from './environment';

export const STORAGE_KEY_PREFIX = 'ff_override_';

const PARENT_CHILD_PREFIXES: Record<string, string> = {
  FF_APP: 'FF_APP_',
};

export interface FeatureFlagEntry {
  key: string;
  description: string;
  buildValue: boolean;
  runtimeOverride?: boolean;
  requiresDebug?: boolean;
  isMockToggle?: boolean;
  localOnly?: boolean;
  parent?: string;
}

export const registry = new Map<string, FeatureFlagEntry>();

/**
 * Checks whether the cockpit is enabled (build flag + non-production runtime).
 */
export const isCockpitEnabled = (): boolean =>
  import.meta.env.FF_DEV_COCKPIT === 'true' && ENVIRONMENT !== 'production';

/**
 * Determines whether a flag is force-disabled by environment constraints
 * (`localOnly` outside `local`, or `requiresDebug` without the cockpit).
 *
 * @param entry The flag entry to evaluate.
 * @returns `true` when the environment blocks the flag.
 */
function isBlockedByEnvironment(entry: FeatureFlagEntry): boolean {
  if (entry.localOnly && ENVIRONMENT !== 'local') return true;
  if (entry.requiresDebug && !isCockpitEnabled()) return true;
  return false;
}

/**
 * Returns the value of a parent flag without triggering cascading checks (avoids recursion).
 */
function getRawValue(entry: FeatureFlagEntry): boolean {
  if (isBlockedByEnvironment(entry)) return false;
  if (entry.runtimeOverride !== undefined) return entry.runtimeOverride;
  return entry.buildValue;
}

/**
 * Checks whether a child flag is disabled because its parent flag (matched by
 * prefix) resolves to false.
 *
 * @param entryKey The child flag key under test.
 * @param parentKey The parent flag key.
 * @param prefix The prefix identifying children of that parent.
 * @returns `true` when the child is blocked by a disabled parent.
 */
function isParentBlocked(entryKey: string, parentKey: string, prefix: string): boolean {
  if (!entryKey.startsWith(prefix) || entryKey === parentKey) return false;
  const parentEntry = registry.get(parentKey);
  return !!parentEntry && !getRawValue(parentEntry);
}

/**
 * Checks whether a flag is blocked by the parent it declares explicitly via
 * `parent`, used when the dependency does not follow the key prefix (e.g.
 * `FF_APP_LEASE` depends on `FF_APP_FUNNEL`).
 *
 * @param entry The flag entry to evaluate.
 * @returns `true` when the declared parent is disabled.
 */
function isBlockedByDeclaredParent(entry: FeatureFlagEntry): boolean {
  if (!entry.parent) return false;
  const parentEntry = registry.get(entry.parent);
  return !!parentEntry && !getRawValue(parentEntry);
}

/**
 * Applies the parent-child cascade: a flag is blocked when its declared parent
 * is disabled, or when any configured parent prefix matches a disabled parent.
 *
 * @param entry The flag entry to evaluate.
 * @returns `true` when a disabled parent blocks this flag.
 */
function isBlockedByParent(entry: FeatureFlagEntry): boolean {
  if (isBlockedByDeclaredParent(entry)) return true;
  return Object.entries(PARENT_CHILD_PREFIXES).some(
    ([parentKey, prefix]) => isParentBlocked(entry.key, parentKey, prefix),
  );
}

/**
 * Computes the final flag value from build, override, debug state, and parent cascading.
 */
export function getResolvedValue(entry: FeatureFlagEntry): boolean {
  if (isBlockedByEnvironment(entry)) return false;
  if (isBlockedByParent(entry)) return false;
  if (entry.runtimeOverride !== undefined) return entry.runtimeOverride;
  return entry.buildValue;
}
